import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import locale from '@angular/common/locales/en';
import localeCL from '@angular/common/locales/es-CL';
import localeCO from '@angular/common/locales/es-CO';
import localePT from '@angular/common/locales/pt';
import { APP_INITIALIZER, importProvidersFrom, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TRANSLATE_HTTP_LOADER_CONFIG, TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AccordionModule } from 'primeng/accordion';
import { MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { FieldsetModule } from 'primeng/fieldset';
import { FileUploadModule } from 'primeng/fileupload';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { Textarea } from 'primeng/inputtextarea';
import { MenubarModule } from 'primeng/menubar';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { PanelMenuModule } from 'primeng/panelmenu';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SidebarModule } from 'primeng/sidebar';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FooterComponent } from './core/footer/footer.component';
import { HeaderComponent } from './core/header/header.component';

import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication
} from '@azure/msal-browser';
import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';
import { providePrimeNG } from 'primeng/config';
import { MenuModule } from 'primeng/menu';
import { PanelModule } from 'primeng/panel';
import { TabViewModule } from 'primeng/tabview';
import { TooltipModule } from 'primeng/tooltip';
import { environment } from '../environments/environment';
import { ReplacePipeModule } from './models/modules/replace-pipe.module';
import { ApsPermissionService } from './services/aps-permission/aps-permission.service';
import { ChoosePlanService } from './services/choose-plan/choose-plan.service';
import { ContractManualService } from './services/contract-manual/contract-manual.service';
import { ContractSummaryService } from './services/contract-summary/contract-summary.service';
import { OrdersPagarMeService } from './services/orders-pagar-me/orders-pagar-me.service';
import { PlanSelecionService } from './services/plan-selection/plan-selection.service';
import { ProductService } from './services/product/product.service';
import { VehicleInformationService } from './services/vehicle-information/vehicle-information.service';
import { VinValidateService } from './services/vin-validate-service/vin-validate-service.service';
import { VinService } from './services/vin/vin.service';
import { CtaButtonComponent } from './shared/components/cta-button/cta-button.component';
import { SignaturePadComponent } from './shared/components/signature-pad/signature-pad.component';
import { ChoosePlanComponent } from './views/choose-plan/choose-plan.component';
import { ClientDataComponent } from './views/client-data/client-data.component';
import { ProcessCancellationComponent } from './views/contract-cancellation/process-cancellation/process-cancellation.component';
import { ViewPendingCancellationsComponent } from './views/contract-cancellation/view-pending-cancellations/view-pending-cancellations.component';
import { ContractEditDealerComponent } from './views/contract-dealer/contract-edit-dealer.component';
import { ContractSearchDealerComponent } from './views/contract-dealer/contract-search-dealer.component';
import { ContractManualComponent } from './views/contract-manual/contract-manual.component';
import { ContractSignatureComponent } from './views/contract-signature/contract-signature.component';
import { ContractSummaryAttachmentOneComponent } from './views/contract-summary-attachment-one/contract-summary-attachment-one.component';
import { ContractSummaryAttachmentTwoComponent } from './views/contract-summary-attachment-two/contract-summary-attachment-two.component';
import { ContractSummaryExternalComponent } from './views/contract-summary-external/contract-summary-external.component';
import { ContractSummaryComponent } from './views/contract-summary/contract-summary.component';
import { ContractEditComponent } from './views/contract/contract-edit.component';
import { ContractSearchComponent } from './views/contract/contract-search.component';
import { ReportsComponent } from './views/excel-reports/reports/reports.component';
import { HomeComponent } from './views/home/home.component';
import { ImportContractComponent } from './views/import-contract/import-contract.component';
import { InformationComponent } from './views/information/information.component';
import { LegalTextContractArgentinaComponent } from './views/legal-text-contract/legal-text-contract-argentina/legal-text-contract-argentina.component';
import { LegalTextContractBraBas1Component } from './views/legal-text-contract/legal-text-contract-bra-bas1/legal-text-contract-bra-bas1.component';
import { LegalTextContractBraDealerReviewsComponent } from './views/legal-text-contract/legal-text-contract-bra-dealer-reviews/legal-text-contract-bra-dealer-reviews.component';
import { LegalTextContractBraPrebComponent } from './views/legal-text-contract/legal-text-contract-bra-preb/legal-text-contract-bra-preb.component';
import { LegalTextContractChileComponent } from './views/legal-text-contract/legal-text-contract-chile/legal-text-contract-chile.component';
import { LegalTextContractColombiaComponent } from './views/legal-text-contract/legal-text-contract-colombia/legal-text-contract-colombia.component';
import { LegalTextContractMaintenanceArgentinaComponent } from './views/legal-text-contract/legal-text-contract-maintenance-argentina/legal-text-contract-maintenance-argentina.component';
import { NavComponent } from './views/nav/nav.component';
import { DirectSaleComponent } from './views/new-contract-direct-sale/direct-sale.component';
import { NotFoundComponent } from './views/not-found/not-found.component';
import { PaymentLegalTextContractBraBas1Component } from './views/payment-legal-text-contract/payment-legal-text-contract-bra-bas1/payment-legal-text-contract-bra-bas1.component';
import { PaymentLegalTextContractBraPrebComponent } from './views/payment-legal-text-contract/payment-legal-text-contract-bra-preb/payment-legal-text-contract-bra-preb.component';
import { PaymentMethodComponent } from './views/payment-method/payment-method.component';
import { PaymentSignatureComponent } from './views/payment-signature/payment-signature.component';
import { PlanSelectionComponent } from './views/plan-selection/plan-selection.component';
import { CoverageDetailsEditComponent } from './views/plans-details-edit/coverage-details-edit/coverage-details-edit.component';
import { EligibilityDetailsEditComponent } from './views/plans-details-edit/eligibility-details-edit/eligibility-details-edit.component';
import { PriceDetailsEditComponent } from './views/plans-details-edit/price-details-edit/price-details-edit.component';
import { ProductDetailsEditComponent } from './views/plans-details-edit/product-details-edit/product-details-edit.component';
import { NewCoverageComponent } from './views/product-create-search/new-coverage/new-coverage.component';
import { NewEligibilityComponent } from './views/product-create-search/new-eligibility/new-eligibility.component';
import { NewPriceComponent } from './views/product-create-search/new-price/new-price.component';
import { NewProductComponent } from './views/product-create-search/new-product/new-product.component';
import { ViewPlansComponent } from './views/product-create-search/view-plans/view-plans.component';
import { SalesChannelExternalComponent } from './views/sales-channel-external/sales-channel-external.component';
import { SalesChannelComponent } from './views/sales-channel/sales-channel.component';
import { VehicleInformationComponent } from './views/vehicle-information/vehicle-information.component';

const FordBluePreset = definePreset(Lara, {
  semantic: {
    primary: {
      50: '#edf3fc',
      100: '#dce8f9',
      200: '#b8d1f3',
      300: '#85b2ea',
      400: '#4d8cde',
      500: '#046cea',
      600: '#0356bb',
      700: '#02418c',
      800: '#012b5d',
      900: '#01162e',
      950: '#000b17'
    }
  }
});

registerLocaleData(localeCL);
registerLocaleData(localeCO);
registerLocaleData(localePT, 'pt-BR');
registerLocaleData(locale, 'en-US');

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: environment.msalConfig.redirectUri,
      postLogoutRedirectUri: environment.msalConfig.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      // loggerOptions: {
      //   loggerCallback,
      //   logLevel: LogLevel.Info,
      //   piiLoggingEnabled: false,
      // },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();
  protectedResourceMap.set(
    environment.myApiConfig.uri,
    environment.myApiConfig.scopes
  )

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.myApiConfig.scopes],
    },
    loginFailedRoute: '/login-failed',
  };
}

export function initializeApsPermissions(apsPermissionService: ApsPermissionService): () => Promise<void> {
  return () => apsPermissionService.bootstrapPermissions();
}

@NgModule({
  declarations: [
    AppComponent,
    NavComponent,
    NotFoundComponent,
    FooterComponent,
    HeaderComponent,
    HomeComponent,
    DirectSaleComponent,
    ImportContractComponent,
    PlanSelectionComponent,
    SalesChannelComponent,
    SalesChannelExternalComponent,
    VehicleInformationComponent,
    ChoosePlanComponent,
    ClientDataComponent,
    ContractSummaryComponent,
    ContractSummaryExternalComponent,
    ContractSummaryAttachmentOneComponent,
    ContractSummaryAttachmentTwoComponent,
    ContractSearchComponent,
    ContractEditComponent,
    ContractManualComponent,
    PaymentMethodComponent,
    PaymentSignatureComponent,
    ContractSearchDealerComponent,
    ContractEditDealerComponent,
    InformationComponent,
    ContractSignatureComponent,
    LegalTextContractBraPrebComponent,
    LegalTextContractChileComponent,
    LegalTextContractBraBas1Component,
    PaymentLegalTextContractBraBas1Component,
    PaymentLegalTextContractBraPrebComponent,
    LegalTextContractColombiaComponent,
    ViewPlansComponent,
    NewProductComponent,
    NewCoverageComponent,
    NewEligibilityComponent,
    NewPriceComponent,
    ProductDetailsEditComponent,
    CoverageDetailsEditComponent,
    PriceDetailsEditComponent,
    EligibilityDetailsEditComponent,
    ReportsComponent,
    LegalTextContractArgentinaComponent,
    LegalTextContractBraDealerReviewsComponent,
    ViewPendingCancellationsComponent,
    ProcessCancellationComponent,
    SignaturePadComponent,
    CtaButtonComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    CalendarModule,
    DatePickerModule,
    AppRoutingModule,
    ButtonModule,
    MenubarModule,
    PanelMenuModule,
    InputSwitchModule,
    SidebarModule,
    ToastModule,
    Textarea,
    TableModule,
    FileUploadModule,
    ProgressBarModule,
    MultiSelectModule,
    DropdownModule,
    AutoCompleteModule,
    RadioButtonModule,
    CardModule,
    AccordionModule,
    InputTextModule,
    CheckboxModule,
    MessageModule,
    FieldsetModule,
    InputMaskModule,
    InputNumberModule,
    DialogModule,
    ProgressSpinnerModule,
    BlockUIModule,
    TabViewModule,
    PanelModule,
    ReplacePipeModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    TooltipModule,
    MenuModule,
    TranslateModule,
    LegalTextContractMaintenanceArgentinaComponent
  ],
  bootstrap: [AppComponent],
  providers: [
    providePrimeNG({
      theme: {
        preset: FordBluePreset,
        options: {
          darkModeSelector: false,
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities'
          }
        }
      }
    }),
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApsPermissions,
      deps: [ApsPermissionService],
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: {
        prefix: './assets/i18n/',
        suffix: '.json'
      }
    },
    importProvidersFrom([
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: TranslateHttpLoader
        }
      })
    ]),
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    VinValidateService,
    MessageService,
    TranslateService,
    PlanSelecionService,
    ProductService,
    VehicleInformationService,
    ContractSummaryService,
    ChoosePlanService,
    VinService,
    ContractManualService,
    OrdersPagarMeService,
    provideAnimations(),
  ],

})

export class AppModule {
}
